import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormHelperText,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Image as ImageIcon } from 'react-feather';
import { useMutation } from '@apollo/client';
import { useDirectUpload } from '@pv/common/hooks';
import {
  AdminCreateOauthApplicationMutation,
  AdminCreateOauthApplicationMutationVariables,
  AdminOauthApplicationFieldsFragment,
  AdminUpdateOauthApplicationMutation,
  AdminUpdateOauthApplicationMutationVariables,
} from '@pv/common/graphql/operations';
import {
  ADMIN_CREATE_OAUTH_APPLICATION_MUTATION,
  ADMIN_UPDATE_OAUTH_APPLICATION_MUTATION,
  evictOauthApplicationLists,
} from './graphql';

const LOGO_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const LOGO_MAX_BYTES = 2 * 1024 * 1024;
const LOGO_ERROR = 'Logos must be PNG, JPEG, or WebP, up to 2 MB.';

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Stack spacing={2}>
    <Typography variant="overline" color="text.secondary" lineHeight={1}>
      {title}
    </Typography>
    {children}
  </Stack>
);

const SettingSwitch = ({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => (
  <Box sx={{ px: 1.75 }}>
    <FormControlLabel
      control={<Switch checked={checked} onChange={onChange} />}
      label={label}
      labelPlacement="start"
      sx={{ ml: 0, width: '100%', justifyContent: 'space-between' }}
    />
    <FormHelperText sx={{ mt: 0 }}>{description}</FormHelperText>
  </Box>
);

interface OauthApplicationFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (oauthApplication: AdminOauthApplicationFieldsFragment) => void;
  oauthApplication?: AdminOauthApplicationFieldsFragment;
  defaultOrganizationPublicId?: string;
}

const formValues = (
  oauthApplication?: AdminOauthApplicationFieldsFragment,
  defaultOrganizationPublicId?: string
) => ({
  name: oauthApplication?.name ?? '',
  redirectUri: oauthApplication?.redirectUri ?? '',
  scopes: oauthApplication?.scopes ?? '',
  confidential: oauthApplication?.confidential ?? true,
  contactEmail: oauthApplication?.contactEmail ?? '',
  organizationPublicId:
    oauthApplication?.organization?.publicId ??
    (oauthApplication ? '' : defaultOrganizationPublicId ?? ''),
  private: oauthApplication?.private ?? false,
  published: oauthApplication?.published ?? false,
});

export const OauthApplicationFormDialog = ({
  open,
  onClose,
  onSaved,
  oauthApplication,
  defaultOrganizationPublicId,
}: OauthApplicationFormDialogProps) => {
  const [values, setValues] = useState(() =>
    formValues(oauthApplication, defaultOrganizationPublicId)
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [logoSignedId, setLogoSignedId] = useState<string | null>();
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const { uploadFile, uploading } = useDirectUpload();

  useEffect(() => {
    if (open) {
      setValues(formValues(oauthApplication, defaultOrganizationPublicId));
      setErrors([]);
      setLogoSignedId(undefined);
      setLogoPreview(oauthApplication?.logoUrl ?? null);
    }
  }, [open, oauthApplication, defaultOrganizationPublicId]);

  const handleLogoChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (
      !LOGO_CONTENT_TYPES.includes(file.type) ||
      file.size >= LOGO_MAX_BYTES
    ) {
      setErrors([LOGO_ERROR]);
      return;
    }

    setErrors([]);
    const signedId = await uploadFile(file);
    if (!signedId) return;

    setLogoSignedId(signedId);
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoSignedId(null);
    setLogoPreview(null);
  };

  const [createOauthApplication, { loading: creating }] = useMutation<
    AdminCreateOauthApplicationMutation,
    AdminCreateOauthApplicationMutationVariables
  >(ADMIN_CREATE_OAUTH_APPLICATION_MUTATION, {
    update: (cache, { data }) => {
      if (data?.adminCreateOauthApplication?.oauthApplication) {
        evictOauthApplicationLists(cache);
      }
    },
  });
  const [updateOauthApplication, { loading: updating }] = useMutation<
    AdminUpdateOauthApplicationMutation,
    AdminUpdateOauthApplicationMutationVariables
  >(ADMIN_UPDATE_OAUTH_APPLICATION_MUTATION, {
    update: (cache, { data }) => {
      if (data?.adminUpdateOauthApplication?.oauthApplication) {
        evictOauthApplicationLists(cache);
      }
    },
  });

  const setValue =
    (field: keyof typeof values) =>
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setValues((current) => ({
        ...current,
        [field]:
          field === 'confidential' ||
          field === 'private' ||
          field === 'published'
            ? event.target.checked
            : event.target.value,
      }));

  const handleSubmit = async () => {
    const input =
      logoSignedId === undefined ? values : { ...values, logoSignedId };
    const result = oauthApplication
      ? (
          await updateOauthApplication({
            variables: { input: { id: oauthApplication.id, ...input } },
          })
        ).data?.adminUpdateOauthApplication
      : (await createOauthApplication({ variables: { input } })).data
          ?.adminCreateOauthApplication;

    if (result?.errors.length) {
      setErrors(result.errors.map((error) => error.message));
    } else if (result?.oauthApplication) {
      onSaved(result.oauthApplication);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {oauthApplication ? 'Edit OAuth app' : 'New OAuth app'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {errors.length > 0 && (
            <Alert severity="error">
              {errors.map((error) => (
                <div key={error}>{error}</div>
              ))}
            </Alert>
          )}
          <Section title="App details">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                variant="rounded"
                src={logoPreview ?? undefined}
                alt="Logo preview"
                imgProps={{ style: { objectFit: 'contain' } }}
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: 'grey.100',
                  color: 'grey.500',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <ImageIcon size={24} />
              </Avatar>
              <Box>
                <Typography variant="subtitle2">Logo</Typography>
                <Typography variant="body2" color="text.secondary">
                  Shown on the consent screen. PNG, JPEG, or WebP, up to 2 MB.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    disabled={uploading}
                  >
                    {uploading
                      ? 'Uploading'
                      : logoPreview
                      ? 'Replace logo'
                      : 'Upload logo'}
                    <input
                      hidden
                      type="file"
                      accept={LOGO_CONTENT_TYPES.join(',')}
                      onChange={handleLogoChange}
                    />
                  </Button>
                  {logoPreview && (
                    <Button
                      size="small"
                      color="error"
                      onClick={removeLogo}
                      disabled={uploading}
                    >
                      Remove logo
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
            <TextField
              label="Name"
              value={values.name}
              onChange={setValue('name')}
              required
            />
            <TextField
              label="Contact email"
              value={values.contactEmail}
              onChange={setValue('contactEmail')}
              helperText="Receives webhook error notifications"
            />
          </Section>
          <Divider />
          <Section title="OAuth settings">
            <TextField
              label="Redirect URIs"
              value={values.redirectUri}
              onChange={setValue('redirectUri')}
              helperText="One per line. Must use https."
              multiline
              minRows={2}
              required
            />
            <TextField
              label="Scopes"
              value={values.scopes}
              onChange={setValue('scopes')}
              helperText="Separated by spaces, for example: read write contacts_read webhook_subscriptions"
            />
            <SettingSwitch
              label="Confidential"
              description="The app keeps its client secret on a server and sends it to get tokens. Turn off for mobile or browser apps that can't keep a secret."
              checked={values.confidential}
              onChange={setValue('confidential')}
            />
          </Section>
          <Divider />
          <Section title="Access">
            <TextField
              label="Organization public ID"
              value={values.organizationPublicId}
              onChange={setValue('organizationPublicId')}
              helperText="Leave blank for a partner app with no owning organization"
            />
            <SettingSwitch
              label="Private"
              description="Only members of the owning organization can connect it."
              checked={values.private}
              onChange={setValue('private')}
            />
            <SettingSwitch
              label="Published"
              description="Staff reviewed. Other organizations can connect it unless it is private."
              checked={values.published}
              onChange={setValue('published')}
            />
          </Section>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={creating || updating || uploading}
        >
          {oauthApplication ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
